// Story 3.7 + 3.8 — /account/preferences page.
//
// FIRST user-preferences hub in the SPA. v1 ships TWO sections:
//   1. animation-preference radio group (Story 3.7) — 3 options
//      (Auto / Reduced / Full) driving useEffectiveMotionMode.
//   2. skip-ceremony-after-graduation toggle (Story 3.8) — conditional
//      checkbox visible once dossiers_opened_count >= 10 per UX-DR33.
//      Below threshold: rendered as a locked-state hint (clearer than
//      a disabled-and-greyed-out toggle — avoids misclick + tells the
//      user WHY the option is missing).
//
// Both sections PATCH /api/v1/account/preferences via useUpdatePreferences
// (single endpoint, multi-field audit shape pre-cemented by Story 3.7).
//
// UX: auto-save-on-change (no save button). Mutation in flight disables
// the controls; success invalidates ["account-me"] so useAccountMe
// re-fetches the new values + useEffectiveMotionMode and
// useEffectiveCeremonyMode pick them up on the next render. Errors
// surface via react-hot-toast (Toaster already mounted in App.tsx).
//
// Layout shell mirrors Story 1.19 DangerZonePage exactly so the account
// area pages cluster visually + the deep-link-only navigation pattern
// (no top-nav link at v1) stays consistent.

import { type ChangeEvent } from "react";
import toast from "react-hot-toast";

import { useAccountMe } from "../features/admin/useAccountMe";
import {
  useUpdatePreferences,
  type AnimationPreferenceValue,
} from "../features/preferences/useUpdatePreferences";
import { TwoFactorSettingsSection } from "../features/two-factor/TwoFactorSettingsSection";

interface RadioOption {
  value: AnimationPreferenceValue;
  label: string;
  description: string;
}

const RADIO_OPTIONS: ReadonlyArray<RadioOption> = [
  {
    value: "auto",
    label: "Auto (match my system)",
    description:
      "Use the operating-system \"Reduce motion\" setting (default).",
  },
  {
    value: "reduced",
    label: "Reduced motion",
    description: "Always use the quick fade transition.",
  },
  {
    value: "full",
    label: "Full ceremony",
    description:
      "Always play the full animated sequence, even when the OS says \"Reduce motion\".",
  },
];

export function AccountPreferencesPage() {
  const accountMe = useAccountMe();
  const mutation = useUpdatePreferences();

  if (accountMe.isLoading) {
    return (
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
        <p className="font-body text-sm opacity-80">Loading…</p>
      </main>
    );
  }

  const current = accountMe.data?.data.animation_preference ?? "auto";
  const dossiersOpenedCount =
    accountMe.data?.data.dossiers_opened_count ?? 0;
  const skipEnabled =
    accountMe.data?.data.skip_ceremony_after_graduation ?? false;
  const graduated = dossiersOpenedCount >= 10;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value as AnimationPreferenceValue;
    if (next === current) return;
    mutation.mutate(
      { animation_preference: next },
      {
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const handleSkipChange = (event: ChangeEvent<HTMLInputElement>) => {
    mutation.mutate(
      { skip_ceremony_after_graduation: event.target.checked },
      {
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
      <header>
        <h1 className="font-heading text-4xl font-bold">Preferences</h1>
        <p className="font-body text-sm opacity-80 mt-2 leading-relaxed">
          Greenhold's reveal ceremony plays a 3-7 second animated sequence
          by default. Reduced-motion mode delivers the same information
          with a quick fade transition.
        </p>
      </header>

      {/* 2FA rework (2026-08-17) — opt-in for everyone, required before
          the admin area opens up for admin accounts. */}
      <TwoFactorSettingsSection />

      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">
          Reveal ceremony animation
        </h2>
        <fieldset
          className="space-y-3"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
        >
          <legend className="sr-only">Animation preference</legend>
          {RADIO_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-start gap-3 cursor-pointer"
            >
              <input
                type="radio"
                name="animation_preference"
                value={option.value}
                checked={current === option.value}
                onChange={handleChange}
                className="mt-1 accent-brand-forest-500"
              />
              <span className="flex flex-col">
                <span className="font-body text-sm font-medium">
                  {option.label}
                </span>
                <span className="font-body text-xs opacity-70">
                  {option.description}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      </section>

      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">
          Quick reveal mode
        </h2>
        <p className="font-body text-sm opacity-80 leading-relaxed">
          Once you've opened 10 dossiers, you unlock a 1.5-second
          compressed ceremony for non-Founder's pulls. Founder's pulls
          always play the full ceremony — that one's too significant
          to compress.
        </p>
        {graduated ? (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={skipEnabled}
              onChange={handleSkipChange}
              disabled={mutation.isPending}
              className="mt-1 accent-brand-forest-500"
            />
            <span className="flex flex-col">
              <span className="font-body text-sm font-medium">
                Enable quick reveal mode for non-Founder's dossiers
              </span>
              <span className="font-body text-xs opacity-70">
                Compresses the ceremony to ~1.5s. Founder's pulls still
                play the full ceremony.
              </span>
            </span>
          </label>
        ) : (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm opacity-70">
            🔒 Available after opening 10 dossiers (you've opened{" "}
            {dossiersOpenedCount}).
          </div>
        )}
      </section>

      <section>
        <p className="text-sm opacity-60">
          More preferences coming soon (locale, notifications, theme).
        </p>
      </section>
    </main>
  );
}
